import { useEffect, useMemo, useState } from 'react';
import { comingSoonTopics, roadmapColor, roadmapVoteKey, type RoadmapTopic } from '@brainfart/shared';
import { Sheet, SheetBody } from './Sheet';
import { AlertsOptIn } from './AlertsOptIn';
import { TopicRequestError, fetchUpcomingVotes, requestTopic, type UpcomingVotes } from '../topicRequests';
import { track } from '../telemetry';

// Only show a count once it means something: "1 player wants this" reads as nobody.
const SHOW_VOTES_FROM = 3;

// Full-colour "coming soon" tiles (a SOON pill sets them apart) under the topic list (behind "Show all topics"). A tap is a vote:
// it is an ordinary topic request for the tile's title, so the people who tap are told
// when it launches, and `npm run topic-requests` shows which topic is wanted most.
// The list is shared/src/roadmap.ts.
export function ComingSoonTopics({ where }: { where: string }) {
  const topics = useMemo(() => comingSoonTopics(), []);
  const [votes, setVotes] = useState<UpcomingVotes>({});
  const [thanks, setThanks] = useState<RoadmapTopic | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!topics.length) return;
    let cancelled = false;
    fetchUpcomingVotes().then(v => { if (!cancelled) setVotes(v); }).catch(() => {});
    return () => { cancelled = true; };
  }, [topics]);

  if (!topics.length) return null;

  const want = async (t: RoadmapTopic) => {
    const key = roadmapVoteKey(t);
    if (busy || votes[key]?.voted) return;
    setBusy(key);
    setError(null);
    try {
      const result = await requestTopic(t.title);
      track({ type: 'topic_requested', payload: { where, votes: result.votes, topic: result.topic } });
      setVotes(v => ({ ...v, [key]: { votes: result.votes, voted: true } }));
      setThanks(t);
    } catch (err) {
      const status = err instanceof TopicRequestError ? err.status : 0;
      setError(status === 429 ? "That's plenty for today. Thanks! Try again tomorrow." : "Couldn't send that. Check your connection and try again.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <section aria-label="Coming soon" data-testid="coming-soon" style={{ marginTop: '0.75rem' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '0.6rem' }}>
        {topics.map(t => {
          const v = votes[roadmapVoteKey(t)];
          const voted = Boolean(v?.voted);
          const color = roadmapColor(t);
          return (
            <button
              key={t.key}
              data-testid="coming-soon-tile"
              data-title={t.title}
              disabled={busy !== null}
              onClick={() => want(t)}
              aria-label={`${t.title}, coming soon. ${voted ? 'You asked for this.' : 'Tap if you want this.'}`}
              style={{
                position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '0.25rem', textAlign: 'left',
                padding: '0.85rem', borderRadius: '14px', cursor: voted ? 'default' : 'pointer', color: '#fff',
                border: `1px dashed ${voted ? '#39FF88' : '#FFC93C88'}`, background: `linear-gradient(135deg, ${color}26, ${color}0d)`,
              }}
            >
              <span style={{ position: 'absolute', top: '0.6rem', right: '0.6rem', fontSize: '0.6rem', fontWeight: 800, letterSpacing: '0.08em', color: '#FFC93C', background: '#FFC93C22', borderRadius: '999px', padding: '0.12rem 0.5rem' }}>
                SOON
              </span>
              <span style={{ fontSize: '1.6rem' }}>{t.icon}</span>
              <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>{t.title}</span>
              <span style={{ color: '#aaa', fontSize: '0.75rem', lineHeight: 1.3 }}>{t.pitch}</span>
              <span style={{ color: voted ? '#39FF88' : '#FFC93C', fontSize: '0.75rem', fontWeight: 700, marginTop: '0.15rem' }}>
                {voted ? '✓ You asked' : '🔔 I want this'}
                {v && v.votes >= SHOW_VOTES_FROM ? ` · ▲ ${v.votes}` : ''}
              </span>
            </button>
          );
        })}
      </div>
      {error && <p style={{ color: '#FF6B6B', fontSize: '0.85rem', marginTop: '0.6rem' }}>{error}</p>}

      {thanks && (
        <Sheet open onClose={() => setThanks(null)} label="Topic requested">
          <SheetBody
            icon="🙌"
            title={`${thanks.title}: noted!`}
            body={`The most-wanted topics get built first, and we'll tell you the moment ${thanks.title} is live.`}
          >
            <AlertsOptIn where="coming_soon" prompt="🔔 Notify me when it launches" doneText="🔔 We'll ping you the moment it's live" />
            <button className="btn btn-primary" onClick={() => setThanks(null)}>Done</button>
          </SheetBody>
        </Sheet>
      )}
    </section>
  );
}
